import type { ReactNode } from "react";

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { DragHandle } from "@similarweb/icons";
import { colorsPalettes, elevation } from "@similarweb/styles";
import { Box, Flex } from "@similarweb/ui-components/foundations";

import type { OverviewWidgetId } from "app/pages/sales-intelligence/pages/management/constants/overviewWidgetsConfig";

import { WidgetRow } from "./WidgetRow";

const SORTABLE_SOURCE_OPACITY_WHILE_DRAGGING = 0.35;

type SortableWidgetRowProps = {
    id: OverviewWidgetId;
    title: string;
    description?: string;
    tooltipContent?: ReactNode;
    isChecked: boolean;
    onToggle: () => void;
    onMouseEnter?: () => void;
    dataAutomation?: string;
};

export const SortableWidgetRow = ({
    id,
    title,
    description,
    tooltipContent,
    isChecked,
    onToggle,
    onMouseEnter,
    dataAutomation,
}: SortableWidgetRowProps) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

    const style = {
        transform: transform == null ? undefined : CSS.Transform.toString({ ...transform, scaleX: 1, scaleY: 1 }),
        transition,
        boxShadow: isDragging ? elevation.Z2 : "none",
        opacity: isDragging ? SORTABLE_SOURCE_OPACITY_WHILE_DRAGGING : 1,
    };

    const dragHandle = (
        <Flex
            {...attributes}
            {...listeners}
            flexShrink="0"
            cursor="grab"
            margin="0 8px 0 4px"
            alignItems="center"
            data-automation={dataAutomation ? `${dataAutomation}-drag-handle` : undefined}
        >
            <DragHandle
                size="xs"
                color={colorsPalettes.carbon["300"]}
            />
        </Flex>
    );

    return (
        <Box
            ref={setNodeRef}
            width="100%"
            borderRadius="8px"
            style={style}
        >
            <WidgetRow
                title={title}
                description={description}
                tooltipContent={tooltipContent}
                isChecked={isChecked}
                onToggle={onToggle}
                onMouseEnter={onMouseEnter}
                dragHandle={dragHandle}
                dataAutomation={dataAutomation}
            />
        </Box>
    );
};
